import { type Component, createMemo, createSignal, For, Show, type VoidComponent } from "solid-js";
import type { RouteSectionProps } from "@solidjs/router";
import withPagePermissionCheck from "../../../utils/withPagePermissionCheck";
import PermissionService from "../../../services/PermissionService";
import { type TranslationKey, useTranslation } from "../../../state/i18n";
import { isEmpty } from "lodash-es";
import { TextAndIconButton } from "../../../components/button/Button";
import InlineMessage from "../../../components/inlineMessage/InlineMessage";
import { MAX_SERVICE_ACCOUNTS } from "../../../constants";
import { Icon } from "../../../components/icon/Icon";
import Text from "../../../components/text/Text";
import { useModalManager } from "../../../components/modal/ModalManager";
import EmptyState from "../../../components/emptyState/EmptyState";
import type { ListItemMenuItem } from "../../../components/listItemMenu/ListItemMenu";
import { IconButtonMenu, ListItemMenu } from "../../../components/listItemMenu/ListItemMenu";
import ServiceAccountService from "../../../services/ServiceAccountService";
import { DestructiveModal } from "../../../components/modal/Modal";
import GenericFailureModal from "../../GenericFailureModal";
import InputModal from "../../../components/modal/InputModal";
import type { InferOutput } from "valibot";
import { TextInput } from "../../../components/inputs/TextInput";
import type { ServiceAccountType } from "../../../types/ServiceAccount";
import { renameServiceAccountSchema } from "../../../types/ServiceAccount";
import type ServiceAccountId from "../../../state/ServiceAccountId";
import type { TFunction } from "i18next";
import { keyFromSelector } from "i18next";
import SettingsPageHeader from "./components/SettingsPageHeader.tsx";

const MONTH_TRANSLATION_KEYS: Record<ServiceAccountType, TranslationKey> = {
  GOOGLE_SHEETS: keyFromSelector(($) => $.SETTINGS_SERVICE_ACCOUNTS_TYPE_GOOGLE_SHEETS),
  ONEDRIVE: keyFromSelector(($) => $.SETTINGS_SERVICE_ACCOUNTS_TYPE_ONEDRIVE),
};

const buildCreateServiceAccountButtonItems = (t: TFunction, createServiceAccount: (type: ServiceAccountType) => void) =>
  [
    [
      {
        value: "create-google-sheets-service-account",
        type: "action",
        variant: "icon",
        label: t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TYPE_GOOGLE_SHEETS),
        icon: { name: "googleSheetsLogo", type: "outline" },
        onClick: () => createServiceAccount("GOOGLE_SHEETS"),
      },
      {
        value: "create-google-onedrive-account",
        type: "action",
        variant: "icon",
        label: t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TYPE_ONEDRIVE),
        icon: { name: "oneDriveLogo", type: "outline" },
        onClick: () => createServiceAccount("ONEDRIVE"),
      },
    ],
  ] satisfies ListItemMenuItem<string>[][];

const ServiceAccountsPage: Component<RouteSectionProps<never>> = () => {
  const { t } = useTranslation();
  const { open, replace } = useModalManager();
  const serviceAccountsQuery = ServiceAccountService.getServiceAccounts();
  const hasReachedServiceAccountLimit = createMemo(() =>
    serviceAccountsQuery.status === "success" ? serviceAccountsQuery.data.length >= MAX_SERVICE_ACCOUNTS : false,
  );
  const [isCreatingServiceAccount, setIsCreatingServiceAccount] = createSignal(false);

  const createServiceAccount = async (type: ServiceAccountType) => {
    try {
      setIsCreatingServiceAccount(true);
      await ServiceAccountService.connectServiceAccount(type);
      void serviceAccountsQuery.refetch();
    } catch (err) {
      open({
        content: () => <GenericFailureModal error={err} />,
      });
    } finally {
      setIsCreatingServiceAccount(false);
    }
  };

  const reconnectServiceAccount = async (serviceAccountId: ServiceAccountId) => {
    try {
      await ServiceAccountService.reconnectServiceAccount(serviceAccountId);
      void serviceAccountsQuery.refetch();
    } catch (err) {
      open({
        content: () => <GenericFailureModal error={err} />,
      });
    }
  };

  return (
    <div class="flex h-full flex-col">
      <SettingsPageHeader
        title={t(($) => $.SETTINGS_SECTION_CONNECTIONS_LABEL)}
        description={t(($) => $.SETTINGS_ITEM_SERVICE_ACCOUNTS_LABEL)}
        actionSlot={() => (
          <Show when={!isEmpty(serviceAccountsQuery.data) && !hasReachedServiceAccountLimit()}>
            <div class="ml-auto">
              <ListItemMenu
                menu={{
                  size: "md",
                  positioning: {
                    placement: "bottom-end",
                  },
                }}
                trigger={(menuTriggerProps) => (
                  <TextAndIconButton
                    variant="light"
                    size="large"
                    text={t(($) => $.SETTINGS_SERVICE_ACCOUNTS_CREATE_BUTTON_LABEL)}
                    icon="plus"
                    loading={isCreatingServiceAccount()}
                    {...menuTriggerProps}
                  />
                )}
                items={buildCreateServiceAccountButtonItems(t, createServiceAccount)}
              />
            </div>
          </Show>
        )}
      />

      <Show when={hasReachedServiceAccountLimit()}>
        <div class="pt-6">
          <InlineMessage
            variant="warning"
            label={t(($) => $.SETTINGS_SERVICE_ACCOUNTS_LIMIT_REACHED, { limit: MAX_SERVICE_ACCOUNTS })}
          />
        </div>
      </Show>

      <div class="relative h-full min-h-0" aria-busy={serviceAccountsQuery.isFetching}>
        <Show
          when={serviceAccountsQuery.data}
          fallback={
            <div class="absolute flex size-full flex-col items-center justify-center bg-surface-default opacity-75">
              <Icon name="spinner" size="2xl" type="solid" class="animate-spin icon-secondary" />
              <span class="text-medium-regular text-primary">
                {t(($) => $.SETTINGS_SERVICE_ACCOUNTS_LOADING_TITLE)}
              </span>
            </div>
          }
        >
          {(data) => (
            <div class="h-full pt-8">
              <Show
                when={!isEmpty(data())}
                fallback={<NoServiceAccounts actionOnClick={(type) => createServiceAccount(type)} />}
              >
                <div
                  role="table"
                  style={{ "--cols": 4 }}
                  class="flex size-full border-collapse flex-col overflow-hidden"
                >
                  <div role="rowgroup">
                    <div
                      role="row"
                      class="grid grid-cols-auto-min-table gap-3 border-b border-subtle text-base-semibold text-secondary"
                    >
                      <div role="columnheader" class="py-1.5">
                        {t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TABLE_HEADER_NAME)}
                      </div>
                      <div role="columnheader" class="py-1.5">
                        {t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TABLE_HEADER_IDENTIFIER)}
                      </div>
                      <div role="columnheader" class="py-1.5">
                        {t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TABLE_HEADER_TYPE)}
                      </div>
                      <div role="columnheader" class="py-1.5">
                        {t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TABLE_HEADER_ACTIONS)}
                      </div>
                    </div>
                  </div>
                  <div role="rowgroup" class="h-full divide-y divide-subtle overflow-y-auto pb-6">
                    <For each={data()}>
                      {(serviceAccount) => (
                        <div
                          role="row"
                          class="grid grid-cols-auto-min-table items-center gap-3 overflow-hidden text-base-regular text-secondary"
                        >
                          <div role="cell" class="flex">
                            <Text class="py-4" text={serviceAccount.name ?? ""} truncate />
                          </div>
                          <div role="cell" class="flex min-w-30">
                            <Text class="py-4" text={serviceAccount.identifier ?? ""} truncate />
                          </div>
                          <div role="cell" class="flex min-w-30">
                            {t(MONTH_TRANSLATION_KEYS[serviceAccount.type])}
                          </div>
                          <div role="cell" class="py-4">
                            <IconButtonMenu
                              aria-label={t(($) => $.ACTIONS_ARIA)}
                              items={[
                                [
                                  {
                                    value: "rename",
                                    type: "action",
                                    variant: "icon",
                                    label: t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TABLE_ACTION_RENAME),
                                    icon: { name: "pencilAlt", type: "outline" },
                                    onClick: async () => {
                                      open({
                                        content: () => (
                                          <InputModal<InferOutput<typeof renameServiceAccountSchema>>
                                            variant="default"
                                            title={t(($) => $.SETTINGS_SERVICE_ACCOUNTS_RENAME_MODAL_TITLE)}
                                            schema={renameServiceAccountSchema}
                                            initialValue={{
                                              name: serviceAccount.name,
                                            }}
                                            confirmLabel={t(
                                              ($) => $.SETTINGS_SERVICE_ACCOUNTS_RENAME_MODAL_ACTIONS_CONFIRM,
                                            )}
                                            cancelLabel={t(
                                              ($) => $.SETTINGS_SERVICE_ACCOUNTS_RENAME_MODAL_ACTIONS_CANCEL,
                                            )}
                                            onConfirm={({ name }) =>
                                              new Promise((resolve, reject) => {
                                                ServiceAccountService.updateServiceAccount(serviceAccount.id).mutate(
                                                  { name },
                                                  {
                                                    onSuccess: () => {
                                                      resolve();
                                                    },
                                                    onError: (err) => {
                                                      replace({
                                                        content: () => <GenericFailureModal error={err} />,
                                                      });
                                                      reject(err);
                                                    },
                                                  },
                                                );
                                              })
                                            }
                                          >
                                            {({ value, updateValue, error }) => (
                                              <TextInput
                                                required
                                                label={t(($) => $.SETTINGS_SERVICE_ACCOUNTS_RENAME_MODAL_INPUT_NAME)}
                                                value={value("name")}
                                                onBlur={(e) => {
                                                  updateValue("name", e.target.value);
                                                }}
                                                onInput={(e) => {
                                                  updateValue("name", e.target.value);
                                                }}
                                                errorMessage={error("name")}
                                              />
                                            )}
                                          </InputModal>
                                        ),
                                      });
                                    },
                                  },
                                  {
                                    value: "reconnect",
                                    type: "action",
                                    variant: "icon",
                                    label: t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TABLE_ACTION_RECONNECT),
                                    icon: { name: "refresh", type: "outline" },
                                    onClick: async () => {
                                      void reconnectServiceAccount(serviceAccount.id);
                                    },
                                  },
                                  {
                                    value: "delete",
                                    type: "action",
                                    variant: "icon",
                                    label: t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TABLE_ACTION_DELETE),
                                    icon: { name: "trash", type: "outline" },
                                    onClick: async () => {
                                      open({
                                        content: () => (
                                          <DestructiveModal
                                            title={t(($) => $.SETTINGS_SERVICE_ACCOUNTS_DELETE_MODAL_TITLE)}
                                            description={t(($) => $.SETTINGS_SERVICE_ACCOUNTS_DELETE_MODAL_DESCRIPTION)}
                                            confirmLabel={t(
                                              ($) => $.SETTINGS_SERVICE_ACCOUNTS_DELETE_MODAL_ACTIONS_CONFIRM,
                                            )}
                                            confirmLabelSlow={t(
                                              ($) => $.SETTINGS_SERVICE_ACCOUNTS_DELETE_MODAL_ACTIONS_CONFIRM_SLOW,
                                            )}
                                            cancelLabel={t(
                                              ($) => $.SETTINGS_SERVICE_ACCOUNTS_DELETE_MODAL_ACTIONS_CANCEL,
                                            )}
                                            onConfirm={() =>
                                              new Promise<void>((resolve, reject) =>
                                                ServiceAccountService.deleteServiceAccount().mutate(serviceAccount.id, {
                                                  onSuccess: () => {
                                                    resolve();
                                                  },
                                                  onError: (err) => {
                                                    replace({
                                                      content: () => <GenericFailureModal error={err} />,
                                                    });
                                                    reject(err);
                                                  },
                                                }),
                                              )
                                            }
                                          />
                                        ),
                                      });
                                    },
                                  },
                                ],
                              ]}
                            />
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </Show>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
};

type NoServiceAccountsProps = {
  actionOnClick: (type: ServiceAccountType) => void;
};
const NoServiceAccounts: VoidComponent<NoServiceAccountsProps> = (props) => {
  const { t } = useTranslation();

  return (
    <EmptyState
      title={t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TABLE_EMPTY_STATE_TITLE)}
      description={t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TABLE_EMPTY_STATE_DESCRIPTION)}
      illustration={{ primaryIcon: "userCircle" }}
      action={{
        variant: "light",
        icon: "plus",
        text: t(($) => $.SETTINGS_SERVICE_ACCOUNTS_TABLE_EMPTY_STATE_ACTION_LABEL),
        items: buildCreateServiceAccountButtonItems(t, props.actionOnClick),
      }}
    />
  );
};

const ProtectedServiceAccountsPage = withPagePermissionCheck(
  ServiceAccountsPage,
  PermissionService.canManageServiceAccounts,
);

export default ProtectedServiceAccountsPage;
